import React, { useMemo } from 'react'
import { twMerge } from 'tailwind-merge'
import { useIntl } from 'react-intl'
import { useBraze, useConfig } from 'msp-integrations'
import { useFlyout } from 'msp-components'
import { IconButton } from '@/components/molecules/IconButton'
import JasminCommerceBox from '@/components/molecules/JasminCommerceBox/JasminCommerceBox'
import { ContentCardBasketType } from './ContentCardBasket.types'

const DEFAULT_BG_COLOR = '#98122B'
const DEFAULT_TEXT_COLOR = '#FFFFFF'

const needsDarkBackground = (bgColor: string) => {
  const color = bgColor.replace('#', '')
  const r = parseInt(color.substring(0, 2), 16)
  const g = parseInt(color.substring(2, 4), 16)
  const b = parseInt(color.substring(4, 6), 16)

  const brightness = (r * 299 + g * 587 + b * 114) / 1000

  return brightness > 128
}

const ContentCardBasket = ({ isDesktop = false }: { isDesktop?: boolean }) => {
  const { basketContentCards, termsConditionsCards } = useBraze()
  const { formatMessage } = useIntl()
  const { openFlyout } = useFlyout()
  const {
    theme: { font, fontVariant }
  } = useConfig()

  let contentCard: ContentCardBasketType | null = null

  if (basketContentCards && basketContentCards.length > 0) {
    contentCard = basketContentCards[0]
  }

  if (!contentCard) return null

  const couponNeedsDarkBackground = useMemo(() => {
    return needsDarkBackground(
      contentCard?.extras?.text_color || DEFAULT_TEXT_COLOR
    )
  }, [contentCard?.extras?.text_color])

  const handleInfoClick = (termId?: string) => {
    if (termId && termsConditionsCards) {
      const termsCard = termsConditionsCards.get(termId)
      if (termsCard) {
        openFlyout(termsCard.title, termsCard.description)
      }
    }
  }

  return (
    <div
      className='text mb-8 mt-2 flex w-full flex-col px-5 py-6 sm:flex-row-reverse sm:px-12 xl:mb-0 xl:mt-6 xl:flex-col xl:px-5'
      style={{
        backgroundColor: contentCard?.extras?.bg_color || DEFAULT_BG_COLOR,
        color: contentCard?.extras?.text_color || DEFAULT_TEXT_COLOR
      }}
    >
      <div className='ml-0 flex flex-col justify-center sm:ml-8 xl:ml-0'>
        {contentCard?.title && (
          <h5
            className={twMerge(
              fontVariant.bold,
              'text-24 text-center font-bold leading-[26px] sm:text-start xl:text-center'
            )}
          >
            {contentCard?.title}
          </h5>
        )}
        {contentCard?.extras?.terms_id && (
          <IconButton
            iconName='InformationNoPadding'
            className={'mx-auto mt-2 flex-shrink-0 sm:mx-0 xl:mx-auto'}
            color={
              contentCard?.extras?.icon_color ||
              contentCard?.extras?.text_color ||
              DEFAULT_TEXT_COLOR
            }
            width='24px'
            height='24px'
            onClick={() => handleInfoClick(contentCard?.extras?.terms_id)}
          />
        )}
        {contentCard?.description && (
          <p
            className={twMerge(
              font.regular,
              'text-14 mt-2 text-center sm:text-start xl:text-center'
            )}
          >
            {contentCard?.description}
          </p>
        )}
        {contentCard?.extras?.coupon_code && (
          <div
            className={twMerge(
              font.regular,
              couponNeedsDarkBackground
                ? 'bg-black bg-opacity-25'
                : 'bg-white bg-opacity-50',
              'mx-auto mt-4 w-fit px-4 py-2 sm:mx-0 xl:mx-auto',
              'text-10 text-center uppercase tracking-[1.4px]'
            )}
          >
            {formatMessage({
              id: 'msp.content_card_basket.coupon.title',
              defaultMessage: 'Aktionscode'
            })}
            :{' '}
            <span className={twMerge(font.bold, 'text-14')}>
              {contentCard?.extras?.coupon_code}
            </span>
          </div>
        )}
        {contentCard?.url && contentCard?.linkText && (
          <a
            href={contentCard?.url}
            target='_blank'
            className={twMerge(
              font.regular,
              'mx-auto mt-4 underline underline-offset-4 sm:mx-0 xl:mx-auto',
              'text-10 text-center uppercase tracking-[3px] sm:text-start xl:text-center'
            )}
          >
            {contentCard?.linkText}
          </a>
        )}
        {!isDesktop && contentCard?.extras?.product_id && (
          <div className='mx-auto flex items-center sm:mx-0 sm:items-start'>
            <JasminCommerceBox
              data={{
                productId: contentCard.extras.product_id,
                type: 'ContentCard',
                addButtonAsIcon: 'XS'
              }}
              showWishListButton={false}
              noPadding
              smallDiscountPrice
              noVariants
            />
          </div>
        )}
      </div>
      <div className='flex flex-col items-center justify-center'>
        {contentCard?.imageUrl && (
          <img
            className={
              'mx-auto mb-4 mt-8 aspect-square h-auto w-full max-w-[282px] object-cover object-center sm:mt-4 xl:mt-8'
            }
            style={
              contentCard?.extras?.image_shadow
                ? { boxShadow: '-19px 26px 37.1px 0 rgba(0, 0, 0, 0.5)' }
                : undefined
            }
            src={contentCard?.imageUrl}
            alt={contentCard?.title}
          />
        )}
        {isDesktop && contentCard?.extras?.product_id && (
          <JasminCommerceBox
            data={{
              productId: contentCard.extras.product_id,
              type: 'ContentCard',
              addButtonAsIcon: 'XS'
            }}
            showWishListButton={false}
            noPadding
            smallDiscountPrice
            noVariants
          />
        )}
      </div>
    </div>
  )
}

export default ContentCardBasket
